/**
 * AdminUserDetailPage — everything on record for one user.
 *
 * Reads GET /api/admin/users/:id (AdminDashboardService.getUserById), which
 * joins users + user_profiles and omits every secret column. Sections render
 * only when they hold at least one value, so a sparse client account doesn't
 * show a wall of empty contractor fields.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
  Button,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  ContentCopy,
  FactCheck,
} from '@mui/icons-material';

import adminService from '../services/adminService';
import RoleChip from '../components/admin/RoleChip';
import UserActions from '../components/admin/UserActions';
import { panelSx, heroPanelSx, panelHeaderSx } from '../components/admin/surfaces';
import { useAuth } from '../contexts/AuthContext';
import { brand, status as statusTokens, text, border } from '../theme/colors';

// The five values users_status_check permits on the live DB.
const STATUS_TONES = {
  active: { fg: statusTokens.successDark, bg: statusTokens.successBg },
  pending: { fg: statusTokens.warningDark, bg: statusTokens.warningBg },
  suspended: { fg: statusTokens.warningDark, bg: statusTokens.warningBg },
  deactivated: { fg: statusTokens.neutral, bg: statusTokens.neutralBg },
  banned: { fg: statusTokens.errorDark, bg: statusTokens.errorBg },
};

const EMPTY = '—';

const formatDate = (value, withTime = false) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const titleise = (value) => (value == null ? EMPTY : String(value).replace(/_/g, ' '));

/** Renders a boolean as an explicit yes/no rather than a bare "true". */
const BoolValue = ({ value }) => {
  if (value == null) return <>{EMPTY}</>;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {value ? (
        <CheckCircle sx={{ fontSize: 16, color: statusTokens.success }} />
      ) : (
        <Cancel sx={{ fontSize: 16, color: text.tertiary }} />
      )}
      <span>{value ? 'Yes' : 'No'}</span>
    </Box>
  );
};

const Field = ({ label, value, span = 6 }) => (
  <Grid item xs={12} sm={span}>
    <Typography
      variant="caption"
      sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: text.primary,
        fontSize: 14,
        mt: 0.25,
        wordBreak: 'break-word',
        textTransform: label === 'Email' ? 'none' : 'capitalize',
      }}
    >
      {value}
    </Typography>
  </Grid>
);

const Section = ({ title, children }) => (
  <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
    <Box sx={panelHeaderSx}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>
      <Grid container spacing={2.5}>
        {children}
      </Grid>
    </Box>
  </Paper>
);

/** True when a section has at least one value worth showing. */
const hasAny = (...values) =>
  values.some((v) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0));

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  // The originating list hands over its path + query so Back returns to the
  // same page of the same list, rather than resetting to page 1 of Users.
  const listSearch = location.state?.listSearch || '';
  const listPath = location.state?.listPath || '/admin/home/users';
  const backLabel = listPath.includes('/contractors')
    ? 'Back to contractors'
    : listPath.includes('/verifiers')
      ? 'Back to verifiers'
      : listPath.includes('/clients')
        ? 'Back to clients'
        : 'Back to users';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getUserById(userId);
      setData(result);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'That user does not exist.'
          : err?.response?.data?.error || err?.message || 'Could not load this user.'
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — nothing useful to show the admin */
    }
  };

  const account = data?.account;
  // Roles that can hold verification assignments (backend VERIFIER_ROLES).
  const isVerifier = ['verifier', 'professional_verifier'].includes(account?.role);
  const profile = data?.profile;
  const name = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  const statusTone = STATUS_TONES[account?.status] || {
    fg: statusTokens.neutral,
    bg: statusTokens.neutralBg,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`${listPath}${listSearch}`)}
        sx={{ textTransform: 'none', color: text.secondary, mb: 2 }}
      >
        {backLabel}
      </Button>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
        </>
      ) : !account ? null : (
        <>
          {/* Identity header */}
          <Paper
            elevation={0}
            sx={{
              ...heroPanelSx,
              p: 2.5,
              mb: 2.5,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2.5,
            }}
          >
            <Avatar
              src={profile?.profileImageUrl || undefined}
              alt={name || account.email}
              sx={{
                width: 72,
                height: 72,
                fontSize: 26,
                fontWeight: 600,
                bgcolor: `${brand.primary}1a`,
                color: brand.primary,
              }}
            >
              {(name || account.email || '?').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
                {name || account.email}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary, mb: 1.25 }}>
                {account.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <RoleChip role={account.role} />
                <Chip
                  size="small"
                  label={titleise(account.status)}
                  sx={{
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    fontSize: 12,
                    color: statusTone.fg,
                    bgcolor: statusTone.bg,
                  }}
                />
                <Typography variant="caption" sx={{ color: text.tertiary, ml: 0.5 }}>
                  Joined {formatDate(account.createdAt)}
                </Typography>
              </Box>
            </Box>

            {/* A verifier's workload lives on its own page — the counts alone
                don't tell you what they are actually carrying. */}
            {isVerifier && (
              <Button
                variant="outlined"
                startIcon={<FactCheck />}
                onClick={() => navigate(`/admin/home/users/${userId}/verifications`)}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Assigned verifications
              </Button>
            )}

            <Tooltip title={copied ? 'Copied' : 'Copy user ID'}>
              <IconButton
                onClick={copyId}
                size="small"
                sx={{ border: `1px solid ${border.light}`, borderRadius: 1.5 }}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Paper>

          {/* Admin actions */}
          <Paper
            elevation={0}
            sx={{ ...panelSx, p: 2.5, mb: 2.5 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: text.tertiary,
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Admin actions
            </Typography>
            <UserActions
              user={account}
              currentUserId={currentUser?.id}
              onChanged={load}
            />
          </Paper>

          {/* Account */}
          <Section title="Account">
            <Field label="User ID" value={account.id} span={12} />
            <Field label="Email" value={account.email} />
            <Field label="Email verified" value={<BoolValue value={account.emailVerified} />} />
            <Field label="Role" value={titleise(account.role)} />
            <Field label="Status" value={titleise(account.status)} />
            <Field label="Sign-in provider" value={titleise(account.authProvider)} />
            <Field label="Account verified" value={<BoolValue value={account.verified} />} />
            <Field label="Verified at" value={formatDate(account.verifiedAt, true)} />
            <Field label="Last login" value={formatDate(account.lastLogin, true)} />
            <Field label="Created" value={formatDate(account.createdAt, true)} />
            <Field label="Updated" value={formatDate(account.updatedAt, true)} />
            <Field label="Failed login attempts" value={account.loginAttempts ?? 0} />
            <Field label="Locked out until" value={formatDate(account.lockoutUntil, true)} />
          </Section>

          {/* Contractors carry a verification file (company + portfolio) that
              lives on the Verifications surface — summarize it here and link
              through, so Users → Contractors reaches the same information. */}
          {data?.contractorVerification && (
            <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
              <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>
                  Contractor verification
                </Typography>
                <Chip
                  size="small"
                  label={titleise(data.contractorVerification.verificationStatus)}
                  sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2.5}>
                  <Field
                    label="Company"
                    value={data.contractorVerification.tradingName
                      || data.contractorVerification.legalName
                      || profile?.companyName
                      || EMPTY}
                  />
                  <Field label="Primary trade" value={titleise(data.contractorVerification.primaryTrade) || EMPTY} />
                  <Field label="Operating address" value={data.contractorVerification.operatingAddress || EMPTY} />
                  <Field label="Submitted" value={formatDate(data.contractorVerification.submittedAt, true)} />
                  <Field label="Portfolio projects" value={data.contractorVerification.portfolioCount} />
                  <Field label="Documents" value={data.contractorVerification.documentCount} />
                </Grid>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
                  onClick={() => navigate(
                    `/admin/home/verifications/contractor/${data.contractorVerification.id}`
                  )}
                >
                  View verification file & portfolio
                </Button>
              </Box>
            </Paper>
          )}

          {!profile ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              This account has no profile record, so there are no further details on file.
            </Alert>
          ) : (
            <>
              {/* Contact */}
              <Section title="Contact & location">
                <Field label="First name" value={profile.firstName || EMPTY} />
                <Field label="Last name" value={profile.lastName || EMPTY} />
                <Field label="Phone" value={profile.phoneNumber || EMPTY} />
                <Field label="Phone verified" value={<BoolValue value={profile.phoneVerified} />} />
                <Field label="City" value={profile.city || EMPTY} />
                <Field label="Country" value={profile.countryCode || EMPTY} />
                <Field label="Address" value={profile.address || EMPTY} span={12} />
                <Field label="Diaspora location" value={profile.diasporaLocation || EMPTY} />
                <Field label="Timezone" value={profile.timezone || EMPTY} />
                <Field label="Date of birth" value={formatDate(profile.dateOfBirth)} />
                <Field label="Website" value={profile.websiteUrl || EMPTY} />
                {profile.bio && <Field label="Bio" value={profile.bio} span={12} />}
              </Section>

              {/* Verification */}
              <Section title="Verification & KYC">
                <Field label="KYC status" value={titleise(profile.kycStatus)} />
                <Field label="KYC verified at" value={formatDate(profile.kycVerifiedAt, true)} />
                <Field
                  label="Identity verified"
                  value={<BoolValue value={profile.identityVerified} />}
                />
                <Field
                  label="Business verified"
                  value={<BoolValue value={profile.businessVerified} />}
                />
                {profile.kycNotes && <Field label="KYC notes" value={profile.kycNotes} span={12} />}
              </Section>

              {/* Business — only for accounts that carry any of it */}
              {hasAny(
                profile.companyName,
                profile.registrationNumber,
                profile.primaryTrade,
                profile.yearsExperience,
                profile.specializations,
                profile.serviceAreas,
                profile.certificationsText,
                profile.suggestedTier
              ) && (
                <Section title="Business & trade">
                  <Field label="Account type" value={titleise(profile.accountType)} />
                  <Field label="Company" value={profile.companyName || EMPTY} />
                  <Field label="Registration no." value={profile.registrationNumber || EMPTY} />
                  <Field label="Primary trade" value={profile.primaryTrade || EMPTY} />
                  <Field
                    label="Years experience"
                    value={profile.yearsExperience ?? EMPTY}
                  />
                  <Field label="Suggested tier" value={titleise(profile.suggestedTier)} />
                  <Field
                    label="Specializations"
                    value={profile.specializations?.length ? profile.specializations.join(', ') : EMPTY}
                    span={12}
                  />
                  <Field
                    label="Service areas"
                    value={profile.serviceAreas?.length ? profile.serviceAreas.join(', ') : EMPTY}
                    span={12}
                  />
                  {profile.certificationsText && (
                    <Field label="Certifications" value={profile.certificationsText} span={12} />
                  )}
                </Section>
              )}

              {/* Licensing */}
              {hasAny(
                profile.contractorLicenseNumber,
                profile.contractorLicenseExpiry,
                profile.insurancePolicyNumber,
                profile.insuranceExpiry
              ) && (
                <Section title="Licensing & insurance">
                  <Field label="Licence no." value={profile.contractorLicenseNumber || EMPTY} />
                  <Field
                    label="Licence expiry"
                    value={formatDate(profile.contractorLicenseExpiry)}
                  />
                  <Field label="Insurance policy" value={profile.insurancePolicyNumber || EMPTY} />
                  <Field label="Insurance expiry" value={formatDate(profile.insuranceExpiry)} />
                </Section>
              )}

              {/* Availability */}
              {hasAny(profile.isAvailable, profile.minJobFee, profile.maxTravelDistance) && (
                <Section title="Availability">
                  <Field label="Available" value={<BoolValue value={profile.isAvailable} />} />
                  <Field
                    label="Minimum job fee"
                    value={profile.minJobFee != null ? `$${profile.minJobFee}` : EMPTY}
                  />
                  <Field
                    label="Max travel distance"
                    value={
                      profile.maxTravelDistance != null ? `${profile.maxTravelDistance} km` : EMPTY
                    }
                  />
                </Section>
              )}

              {/* Local representative */}
              {hasAny(
                profile.localRepresentativeName,
                profile.localRepresentativePhone,
                profile.localRepresentativeRelationship
              ) && (
                <Section title="Local representative">
                  <Field label="Name" value={profile.localRepresentativeName || EMPTY} />
                  <Field label="Phone" value={profile.localRepresentativePhone || EMPTY} />
                  <Field
                    label="Relationship"
                    value={titleise(profile.localRepresentativeRelationship)}
                  />
                </Section>
              )}

              {/* Preferences */}
              <Section title="Preferences">
                <Field label="Language" value={profile.language || EMPTY} />
                <Field label="Currency" value={profile.currencyPreference || EMPTY} />
                <Field label="Payment method" value={titleise(profile.preferredPaymentMethod)} />
                <Field
                  label="Remittance provider"
                  value={titleise(profile.remittanceProviderPreference)}
                />
                {profile.communicationPreferences && (
                  <Field
                    label="Communication"
                    value={
                      Object.entries(profile.communicationPreferences)
                        .filter(([, on]) => on)
                        .map(([channel]) => channel)
                        .join(', ') || 'None enabled'
                    }
                    span={12}
                  />
                )}
              </Section>

              {/* Onboarding */}
              <Section title="Onboarding">
                <Field label="Welcome seen" value={<BoolValue value={profile.welcomeSeen} />} />
                <Field
                  label="Onboarding complete"
                  value={<BoolValue value={profile.onboardingComplete} />}
                />
                <Field label="Phase" value={profile.onboardingPhase ?? EMPTY} />
              </Section>

              {/* Disputes — only when the user is actually on a dispute team */}
              {hasAny(account.disputeTeamLevel, account.canHandleDisputes || null) && (
                <Section title="Disputes">
                  <Field label="Team level" value={titleise(account.disputeTeamLevel)} />
                  <Field
                    label="Can handle disputes"
                    value={<BoolValue value={account.canHandleDisputes} />}
                  />
                  <Field label="Cases handled" value={account.disputeCaseCount ?? 0} />
                </Section>
              )}
            </>
          )}

          <Divider sx={{ my: 1 }} />
        </>
      )}
    </Box>
  );
};

export default AdminUserDetailPage;
